import React, { memo, useEffect, useRef, useState } from 'react';
import { Box, Typography, Tooltip, Chip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CloseIcon from '@mui/icons-material/Close';
import UndoIcon from '@mui/icons-material/Undo';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import { getApplicationStyle, APPLICATION_TYPES, categoryTagsForDisplay } from './applicationStyles';
import { getCaseStatusColor, STATUS_BADGE_LABELS } from '../utils/caseStatus';
import {
  getTimelineEventList,
  filterEventsByMode,
  eventBubbleDiameter,
  ICON_EVENT_TYPES,
  MILESTONE_EVENT_TYPES,
  markerIconFontSizePx,
  tooltipTitleLine,
  truncateNotes,
} from '../utils/timelineEvents';
import { parseLocalDateString, formatLocalDateString } from '../utils/localDate';

const SPINE_HEIGHT = '3px';

const isValidDate = (d) => d instanceof Date && !isNaN(d);

export const TruncatedText = ({ children, sx, ...props }) => {
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [children]);

  const typography = (
    <Typography
      ref={textRef}
      component="div"
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
        ...sx,
      }}
      {...props}
    >
      {children}
    </Typography>
  );

  if (isTruncated) {
    return (
      <Tooltip title={children} placement="top" enterDelay={300} arrow>
        {typography}
      </Tooltip>
    );
  }

  return typography;
};

const coreIconFor = (status) => {
  switch (status) {
    case 'initial_filing': return <PlayArrowIcon />;
    case 'recommendation_approval': return <ThumbUpAltOutlinedIcon />;
    case 'recommendation_denial': return <ThumbDownAltOutlinedIcon />;
    case 'approved': return <CheckIcon />;
    case 'denied': return <CloseIcon />;
    case 'withdrawn': return <UndoIcon />;
    default: return <RadioButtonUncheckedIcon />;
  }
};

const ganttIconSx = (eventType, fsPx) => {
  return { fontSize: `${fsPx}px`, color: 'white', '& path': { stroke: 'white', strokeWidth: 2 } };
};

const buildEventTooltip = (event) => {
  const et = String(event.event_type || '').toLowerCase();
  const label = (event.label || '').trim();
  const showLabelLine =
    label &&
    label.toLowerCase() !== et &&
    label.toLowerCase() !== tooltipTitleLine(event).toLowerCase();
  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 700 }} display="block">
        {tooltipTitleLine(event)}
      </Typography>
      <Typography variant="caption" display="block">
        Date: {formatLocalDateString(event.date)}
      </Typography>
      {showLabelLine && (
        <Typography variant="caption" display="block">
          {event.label}
        </Typography>
      )}
      {event.body ? (
        <Typography variant="caption" display="block">
          {event.body}
        </Typography>
      ) : null}
      {event.vote ? (
        <Typography variant="caption" display="block">
          Vote: {event.vote}
        </Typography>
      ) : null}
      {event.notes ? (
        <Typography variant="caption" display="block">
          {truncateNotes(event.notes)}
        </Typography>
      ) : null}
    </Box>
  );
};

const GanttTimelineRow = memo(function GanttTimelineRow({
  app,
  isSelected,
  milestonesOnly,
  dateRange,
  showSideCols,
  APP_COL_W,
  SIDE_COL_W,
  TL_PAD_L,
  TL_PAD_R,
  getPosition,
  clampPercent,
  onSelect,
  /** Phone-narrow: stack status above case # so the apps col stays legible. */
  stackStatus = false,
}) {
  const allEvents = getTimelineEventList(app.timeline);
  const visibleEvents = filterEventsByMode(allEvents, milestonesOnly);
  const validStages = visibleEvents
    .map((event, idx) => {
      const d = parseLocalDateString(event.date);
      return d && isValidDate(d) ? { event, idx, d } : null;
    })
    .filter(Boolean);

  const stagesByDay = {};
  validStages.forEach(({ event, idx, d }) => {
    const key = String(event.date || '').slice(0, 10) || 'unknown';
    if (!stagesByDay[key]) stagesByDay[key] = [];
    stagesByDay[key].push({ ...event, originalIndex: idx, _d: d });
  });

  const stackMeta = {};
  let maxStackForApp = 1;
  Object.values(stagesByDay).forEach((arr) => {
    maxStackForApp = Math.max(maxStackForApp, arr.length);
    arr.forEach((entry, pos) => {
      stackMeta[entry.originalIndex] = { stackIndex: pos, stackTotal: arr.length };
    });
  });

  const offsetStep = 12;
  const baseRowHeight = 50;
  let maxBubble = 30;
  validStages.forEach(({ event }) => {
    maxBubble = Math.max(maxBubble, eventBubbleDiameter(event));
  });
  const bubbleDiameter = maxBubble;
  const rowHeight = Math.max(baseRowHeight, bubbleDiameter + (maxStackForApp - 1) * offsetStep + 20);

  const appTypes = app.category_names?.length > 0 ? categoryTagsForDisplay(app.category_names) : [];
  const statusColor = getCaseStatusColor(app.current_status);

  // Split stages into Past / In-range / Future for the 3-column ranged view.
  const minD = dateRange?.minDate ? new Date(dateRange.minDate) : null;
  const maxD = dateRange?.maxDate ? new Date(dateRange.maxDate) : null;
  const sortedValidStages = [...validStages].sort((a, b) => a.d - b.d);
  const pastStagesAll =
    showSideCols && minD ? sortedValidStages.filter(x => x.d < minD) : [];
  const futureStagesAll =
    showSideCols && maxD ? sortedValidStages.filter(x => x.d > maxD) : [];
  const inRangeStages =
    showSideCols && minD && maxD
      ? sortedValidStages.filter(x => x.d >= minD && x.d <= maxD)
      : sortedValidStages;

  /** All events (ignore tier filter) — used only for spine line start/end and side-column line span. */
  const allValidStages = allEvents
    .map((event, idx) => {
      const d = parseLocalDateString(event.date);
      return d && isValidDate(d) ? { event, idx, d } : null;
    })
    .filter(Boolean);
  const sortedAllStages = [...allValidStages].sort((a, b) => a.d - b.d);
  const pastStagesAllFull =
    showSideCols && minD ? sortedAllStages.filter(x => x.d < minD) : [];
  const futureStagesAllFull =
    showSideCols && maxD ? sortedAllStages.filter(x => x.d > maxD) : [];
  const inRangeStagesFull =
    showSideCols && minD && maxD
      ? sortedAllStages.filter(x => x.d >= minD && x.d <= maxD)
      : sortedAllStages;

  // How many bubbles fit in the side columns (rough + deterministic).
  const SIDE_ARROW_PX = 14;
  const SIDE_PAD_PX = 10;
  const SIDE_STEP_PX = bubbleDiameter + 8; // spacing between bubbles in side columns
  const sideAvail = Math.max(0, SIDE_COL_W - SIDE_PAD_PX - SIDE_ARROW_PX);
  const sideMaxCount = showSideCols ? Math.max(0, Math.floor(sideAvail / SIDE_STEP_PX) + 1) : 0;

  // In Past: show most recent past items closest to the range boundary (right side).
  const pastShown = showSideCols
    ? pastStagesAll.slice(Math.max(0, pastStagesAll.length - sideMaxCount)).reverse()
    : [];

  // In Future: show earliest future items closest to the range boundary (left side).
  const futureShown = showSideCols ? futureStagesAll.slice(0, sideMaxCount) : [];

  /** Same side-column slice logic as pastShown / futureShown, but on full timeline (for line geometry). */
  const pastShownFull = showSideCols
    ? pastStagesAllFull.slice(Math.max(0, pastStagesAllFull.length - sideMaxCount)).reverse()
    : [];
  const pastOverflowFull = showSideCols ? pastStagesAllFull.length > pastShownFull.length : false;
  const futureShownFull = showSideCols ? futureStagesAllFull.slice(0, sideMaxCount) : [];
  const futureOverflowFull = showSideCols ? futureStagesAllFull.length > futureShownFull.length : false;

  const posRangeFor = (arr) => {
    if (!arr || arr.length === 0) return { min: null, max: null };
    let min = null;
    let max = null;
    arr.forEach((x) => {
      if (!x) return;
      const p = clampPercent(getPosition(x.d));
      if (min === null || p < min) min = p;
      if (max === null || p > max) max = p;
    });
    return { min, max };
  };

  // Central column spine uses full timeline date extent; bubbles still use tier-filtered stages.
  const inRangePosFull = posRangeFor(inRangeStagesFull);

  return (
    <Box
      onClick={() => onSelect(app)}
      sx={{
        display: 'grid',
        gridTemplateColumns: `${APP_COL_W}px 1fr`,
        minHeight: `${rowHeight}px`,
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: isSelected ? 'rgba(116,84,165,0.08)' : 'transparent',
        // Use an inset shadow instead of a border so we don't shift layout.
        boxShadow: isSelected ? 'inset 3px 0 0 #7454A5' : 'none',
        cursor: 'pointer',
        transition: 'background-color 140ms ease',
        // Trap bubble z-index (1000+) so sticky header stays on top when scrolling.
        position: 'relative',
        zIndex: 0,
        '&:hover': {
          // darker + translucent so the grid doesn't "white out"
          backgroundColor: isSelected ? 'rgba(116,84,165,0.12)' : 'rgba(116,84,165,0.06)',
        },
      }}
    >
      {/* Applications column — phone: status stacked above case #; desktop: case+types | status */}
      {stackStatus ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.35,
            px: 1.25,
            py: 0.75,
            minWidth: 0,
            minHeight: '100%',
            height: '100%',
            overflow: 'hidden',
            justifyContent: 'center',
          }}
        >
          {app.current_status ? (
            <Chip
              label={STATUS_BADGE_LABELS[app.current_status] || String(app.current_status).toUpperCase()}
              size="small"
              sx={{
                height: 18,
                maxWidth: '100%',
                alignSelf: 'flex-start',
                fontSize: '0.58rem',
                fontWeight: 700,
                bgcolor: `${statusColor}18`,
                color: statusColor,
                borderRadius: '4px',
                '& .MuiChip-label': {
                  px: 0.75,
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
            />
          ) : null}
          <TruncatedText
            variant="caption"
            sx={{
              fontWeight: isSelected ? 800 : 700,
              color: isSelected ? '#4a2f77' : '#333',
              width: '100%',
              fontSize: '0.75rem',
            }}
          >
            {app.case_number || app.id}
          </TruncatedText>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', minWidth: 0 }}>
            {appTypes.map((type, i) => {
              const typeColor = APPLICATION_TYPES[type]?.labelColor || APPLICATION_TYPES[type]?.color || getApplicationStyle(type).color || '#7454A5';
              return (
                <Chip
                  key={i}
                  label={type}
                  size="small"
                  title={type}
                  sx={{
                    height: 18,
                    maxWidth: '100%',
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    bgcolor: `${typeColor}15`,
                    color: typeColor,
                    borderRadius: '4px',
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block',
                      px: 0.65,
                    },
                  }}
                />
              );
            })}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) auto',
            alignItems: 'stretch',
            columnGap: 1,
            px: 2,
            py: 0.75,
            minWidth: 0,
            minHeight: '100%',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, minWidth: 0, justifyContent: 'center' }}>
            <TruncatedText
              variant="caption"
              sx={{
                fontWeight: isSelected ? 800 : 700,
                color: isSelected ? '#4a2f77' : '#333',
                width: '100%',
                fontSize: '0.75rem',
              }}
            >
              {app.case_number || app.id}
            </TruncatedText>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', minWidth: 0 }}>
              {appTypes.map((type, i) => {
                const typeColor = APPLICATION_TYPES[type]?.labelColor || APPLICATION_TYPES[type]?.color || getApplicationStyle(type).color || '#7454A5';
                return (
                  <Chip
                    key={i}
                    label={type}
                    size="small"
                    title={type}
                    sx={{
                      height: 20,
                      maxWidth: '100%',
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      bgcolor: `${typeColor}15`,
                      color: typeColor,
                      borderRadius: '4px',
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                        px: 0.75,
                      },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              alignSelf: 'stretch',
              flexShrink: 0,
              ml: 'auto',
              minWidth: 0,
              maxWidth: 180,
            }}
          >
            {app.current_status ? (
              <Chip
                label={STATUS_BADGE_LABELS[app.current_status] || String(app.current_status).toUpperCase()}
                size="small"
                sx={{
                  height: 20,
                  maxWidth: '100%',
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  bgcolor: `${statusColor}18`,
                  color: statusColor,
                  borderRadius: '4px',
                  '& .MuiChip-label': {
                    px: 0.85,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                  },
                }}
              />
            ) : null}
          </Box>
        </Box>
      )}

      {/* Timeline column cell */}
      <Box
        sx={{
          position: 'relative',
          minWidth: 0,
          display: 'grid',
          gridTemplateColumns: showSideCols ? `${SIDE_COL_W}px 1fr ${SIDE_COL_W}px` : '1fr',
        }}
      >
        {showSideCols && (
          <Box sx={{ position: 'relative', minWidth: 0 }}>
            {/* Past column line + terminating arrow (only if overflow) */}
            {pastStagesAllFull.length > 0 && pastShownFull.length > 0 && (
              <>
                {(() => {
                  // Past bubbles are placed from right -> left; the "oldest shown" is the leftmost.
                  // Line span uses full timeline so it persists when tier-filtered bubbles hide.
                  const oldestShownPct = clampPercent(
                    ((Math.max(0, SIDE_COL_W - SIDE_PAD_PX - (pastShownFull.length - 1) * SIDE_STEP_PX)) /
                      SIDE_COL_W) *
                      100
                  );
                  const lineLeft = pastOverflowFull ? 0 : oldestShownPct;
                  const lineRight = 100; // always connects to the range boundary (right edge)
                  return (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: `${lineLeft}%`,
                        right: `${100 - lineRight}%`,
                        top: '50%',
                        height: SPINE_HEIGHT,
                        backgroundColor: statusColor,
                        transform: 'translateY(-50%)',
                        opacity: 0.9,
                        zIndex: 1,
                      }}
                    />
                  );
                })()}
                {pastOverflowFull && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: '50%',
                      transform: 'translate(0, -50%)',
                      width: 0,
                      height: 0,
                      borderTop: '6px solid transparent',
                      borderBottom: '6px solid transparent',
                      borderRight: `10px solid ${statusColor}`,
                      zIndex: 2,
                    }}
                  />
                )}
              </>
            )}

            {/* Past bubbles (closest to range boundary on the right) */}
            {(() => {
              const prepared = pastShown.map(({ event, idx: stageIndex, d }, i) => {
                const xPx = Math.max(0, SIDE_COL_W - SIDE_PAD_PX - i * SIDE_STEP_PX);
                const position = clampPercent((xPx / SIDE_COL_W) * 100);
                return { event, stageIndex, d, position };
              });
              prepared.sort((a, b) => {
                if (a.position !== b.position) return a.position - b.position;
                return a.stageIndex - b.stageIndex;
              });
              return prepared.map(({ event, stageIndex, position }) => {
                const et = String(event.event_type || '').toLowerCase();
                const diam = eventBubbleDiameter(event);
                const showIcon = ICON_EVENT_TYPES.has(et);
                const isMilestone = MILESTONE_EVENT_TYPES.has(et);
                const icon = coreIconFor(et);
                const meta = stackMeta[stageIndex] || { stackIndex: 0, stackTotal: 1 };
                const offsetFromCenter = (meta.stackIndex - (meta.stackTotal - 1) / 2) * offsetStep;
                const fs = markerIconFontSizePx(et);
                const z = (showIcon ? 1000 : 2000) + Math.round(position);
                return (
                  <Tooltip
                    key={`past-${stageIndex}`}
                    title={buildEventTooltip(event)}
                    placement="top"
                    arrow
                    enterDelay={100}
                    enterTouchDelay={0}
                    slotProps={{
                      popper: { sx: { zIndex: 3000 } },
                      tooltip: { sx: { bgcolor: '#221a36', color: 'white', border: '1px solid #7454A5', zIndex: 3000 } },
                      arrow: { sx: { color: '#221a36' } },
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        left: `${position}%`,
                        top: `calc(50% + ${offsetFromCenter}px)`,
                        transform: 'translateX(-50%)',
                        zIndex: z,
                        cursor: 'pointer',
                        pointerEvents: 'auto',
                      }}
                      onClick={() => onSelect(app)}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box
                          sx={{
                            color: 'white',
                            backgroundColor: statusColor,
                            borderRadius: '50%',
                            width: diam,
                            height: diam,
                            mt: `-${diam / 2}px`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            outline: '1.5px solid rgba(255,255,255,0.92)',
                            boxShadow:
                              isMilestone
                                ? '0 0 0 1px rgba(0,0,0,0.35), 0 4px 10px rgba(0,0,0,0.25)'
                                : '0 0 0 1px rgba(0,0,0,0.35), 0 2px 6px rgba(0,0,0,0.22)',
                            border: 0,
                          }}
                        >
                          {showIcon
                            ? React.cloneElement(icon, { sx: ganttIconSx(et, fs) })
                            : null}
                        </Box>
                        {event.vote ? (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.6rem',
                              color: '#555',
                              mt: 0.35,
                              lineHeight: 1,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {event.vote}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  </Tooltip>
                );
              });
            })()}
          </Box>
        )}

        {/* Range column (in-range timeline) */}
        <Box sx={{ position: 'relative', minWidth: 0 }}>
          <Box sx={{ position: 'absolute', left: TL_PAD_L, right: TL_PAD_R, top: 0, bottom: 0, zIndex: 1 }}>
            {/* Timeline line for this application (colored by case status) */}
            {(() => {
              const hasAny = sortedAllStages.length > 1;
              if (!hasAny) return null;
              const color = statusColor;
              const left = pastStagesAllFull.length > 0 ? 0 : (inRangePosFull.min ?? 0);
              const right = futureStagesAllFull.length > 0 ? 100 : (inRangePosFull.max ?? 100);
              // If we don't have any in-range bubbles (possible with very narrow ranges),
              // still draw a full line only if it continues into Past/Future.
              if (
                inRangeStagesFull.length === 0 &&
                pastStagesAllFull.length === 0 &&
                futureStagesAllFull.length === 0
              ) {
                return null;
              }
              return (
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${left}%`,
                    right: `${100 - right}%`,
                    top: '50%',
                    height: SPINE_HEIGHT,
                    backgroundColor: color,
                    transform: 'translateY(-50%)',
                    zIndex: 1,
                    opacity: 0.9,
                  }}
                />
              );
            })()}

            {/* In-range bubbles */}
            {(() => {
              const prepared = inRangeStages.map(({ event, idx: stageIndex, d }) => {
                const position = clampPercent(getPosition(d));
                return { event, stageIndex, d, position };
              });
              prepared.sort((a, b) => {
                if (a.position !== b.position) return a.position - b.position;
                return a.stageIndex - b.stageIndex;
              });

              return prepared.map(({ event, stageIndex, position }) => {
                const et = String(event.event_type || '').toLowerCase();
                const diam = eventBubbleDiameter(event);
                const showIcon = ICON_EVENT_TYPES.has(et);
                const isMilestone = MILESTONE_EVENT_TYPES.has(et);
                const icon = coreIconFor(et);
                const meta = stackMeta[stageIndex] || { stackIndex: 0, stackTotal: 1 };
                const offsetFromCenter = (meta.stackIndex - (meta.stackTotal - 1) / 2) * offsetStep;
                const fs = markerIconFontSizePx(et);
                const z = (showIcon ? 1000 : 2000) + Math.round(position);

                return (
                  <Tooltip
                    key={`range-${stageIndex}`}
                    title={buildEventTooltip(event)}
                    placement="top"
                    arrow
                    enterDelay={100}
                    enterTouchDelay={0}
                    slotProps={{
                      popper: { sx: { zIndex: 3000 } },
                      tooltip: { sx: { bgcolor: '#221a36', color: 'white', border: '1px solid #7454A5', zIndex: 3000 } },
                      arrow: { sx: { color: '#221a36' } },
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        left: `${position}%`,
                        top: `calc(50% + ${offsetFromCenter}px)`,
                        transform: 'translateX(-50%)',
                        zIndex: z,
                        cursor: 'pointer',
                        pointerEvents: 'auto',
                      }}
                      onClick={() => onSelect(app)}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box
                          sx={{
                            color: 'white',
                            backgroundColor: statusColor,
                            borderRadius: '50%',
                            width: diam,
                            height: diam,
                            mt: `-${diam / 2}px`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            outline: '1.5px solid rgba(255,255,255,0.92)',
                            boxShadow:
                              isMilestone
                                ? '0 0 0 1px rgba(0,0,0,0.35), 0 4px 10px rgba(0,0,0,0.25)'
                                : '0 0 0 1px rgba(0,0,0,0.35), 0 2px 6px rgba(0,0,0,0.22)',
                            border: 0,
                            '&:hover': {
                              transform: 'scale(1.15)',
                              transition: 'transform 0.2s',
                            },
                          }}
                        >
                          {showIcon
                            ? React.cloneElement(icon, { sx: ganttIconSx(et, fs) })
                            : null}
                        </Box>
                        {event.vote ? (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.6rem',
                              color: '#555',
                              mt: 0.35,
                              lineHeight: 1,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {event.vote}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  </Tooltip>
                );
              });
            })()}
          </Box>
        </Box>

        {showSideCols && (
          <Box sx={{ position: 'relative', minWidth: 0 }}>
            {/* Future column line + terminating arrow (only if overflow) */}
            {futureStagesAllFull.length > 0 && futureShownFull.length > 0 && (
              <>
                {(() => {
                  // Future bubbles are placed from left -> right; the "latest shown" is the rightmost.
                  const latestShownPct = clampPercent(
                    ((Math.min(SIDE_COL_W, SIDE_PAD_PX + (futureShownFull.length - 1) * SIDE_STEP_PX)) /
                      SIDE_COL_W) *
                      100
                  );
                  const lineLeft = 0; // always connects to the range boundary (left edge)
                  const lineRight = futureOverflowFull ? 100 : latestShownPct;
                  return (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: `${lineLeft}%`,
                        right: `${100 - lineRight}%`,
                        top: '50%',
                        height: SPINE_HEIGHT,
                        backgroundColor: statusColor,
                        transform: 'translateY(-50%)',
                        opacity: 0.9,
                        zIndex: 1,
                      }}
                    />
                  );
                })()}
                {futureOverflowFull && (
                  <Box
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: '50%',
                      transform: 'translate(0, -50%)',
                      width: 0,
                      height: 0,
                      borderTop: '6px solid transparent',
                      borderBottom: '6px solid transparent',
                      borderLeft: `10px solid ${statusColor}`,
                      zIndex: 2,
                    }}
                  />
                )}
              </>
            )}

            {/* Future bubbles (closest to range boundary on the left) */}
            {(() => {
              const prepared = futureShown.map(({ event, idx: stageIndex, d }, i) => {
                const xPx = Math.min(SIDE_COL_W, SIDE_PAD_PX + i * SIDE_STEP_PX);
                const position = clampPercent((xPx / SIDE_COL_W) * 100);
                return { event, stageIndex, d, position };
              });
              prepared.sort((a, b) => {
                if (a.position !== b.position) return a.position - b.position;
                return a.stageIndex - b.stageIndex;
              });
              return prepared.map(({ event, stageIndex, position }) => {
                const et = String(event.event_type || '').toLowerCase();
                const diam = eventBubbleDiameter(event);
                const showIcon = ICON_EVENT_TYPES.has(et);
                const isMilestone = MILESTONE_EVENT_TYPES.has(et);
                const icon = coreIconFor(et);
                const meta = stackMeta[stageIndex] || { stackIndex: 0, stackTotal: 1 };
                const offsetFromCenter = (meta.stackIndex - (meta.stackTotal - 1) / 2) * offsetStep;
                const fs = markerIconFontSizePx(et);
                const z = (showIcon ? 1000 : 2000) + Math.round(position);
                return (
                  <Tooltip
                    key={`future-${stageIndex}`}
                    title={buildEventTooltip(event)}
                    placement="top"
                    arrow
                    enterDelay={100}
                    enterTouchDelay={0}
                    slotProps={{
                      popper: { sx: { zIndex: 3000 } },
                      tooltip: { sx: { bgcolor: '#221a36', color: 'white', border: '1px solid #7454A5', zIndex: 3000 } },
                      arrow: { sx: { color: '#221a36' } },
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        left: `${position}%`,
                        top: `calc(50% + ${offsetFromCenter}px)`,
                        transform: 'translateX(-50%)',
                        zIndex: z,
                        cursor: 'pointer',
                        pointerEvents: 'auto',
                      }}
                      onClick={() => onSelect(app)}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box
                          sx={{
                            color: 'white',
                            backgroundColor: statusColor,
                            borderRadius: '50%',
                            width: diam,
                            height: diam,
                            mt: `-${diam / 2}px`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            outline: '1.5px solid rgba(255,255,255,0.92)',
                            boxShadow:
                              isMilestone
                                ? '0 0 0 1px rgba(0,0,0,0.35), 0 4px 10px rgba(0,0,0,0.25)'
                                : '0 0 0 1px rgba(0,0,0,0.35), 0 2px 6px rgba(0,0,0,0.22)',
                            border: 0,
                          }}
                        >
                          {showIcon
                            ? React.cloneElement(icon, { sx: ganttIconSx(et, fs) })
                            : null}
                        </Box>
                        {event.vote ? (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.6rem',
                              color: '#555',
                              mt: 0.35,
                              lineHeight: 1,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {event.vote}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  </Tooltip>
                );
              });
            })()}
          </Box>
        )}
      </Box>
    </Box>
  );
});

export default GanttTimelineRow;
